import {ClassificationResult} from '../ClassificationResult';
import {SegmentationResult} from '../SegmentationResult';
import {PoseDetectionResult} from '../PoseDetectionResult';
import {DetectionResult, Image} from '../DetectionResult';
import React, {useMemo} from 'react';
import {UnparsedJson} from '../../../types/utility/UnparsedJson';
import {DeploymentDetailsInfo} from '../../../types/deployment/DeploymentDetailsInfo';
import {DeploymentDetectionResult} from '../../../types/deployment/DeploymentDetectionResult';
import {DeploymentDetectionResultStats} from '../../../types/deployment/DeploymentDetectionResultStats';
import URL, {MODEL_SUB_OP} from '../../../config/url';
import {DeployConfigState} from '../useDeploy';
import {startCase, toLower} from 'lodash';
import {Project} from '../../../types/project/Project';

export type StaticTestResultsProps = {
  projectId: string;
  search: string;
  deployment: DeploymentDetailsInfo;
  deployConfig: DeployConfigState;
  projectData: Project;
};

export const StaticTestResults = ({
  projectId,
  search,
  deployment,
  deployConfig,
  projectData,
}: StaticTestResultsProps) => {
  const inferenceResults: UnparsedJson | undefined = useMemo(
    () => deployment.inferenceResults,
    [deployment.inferenceResults]
  );

  const parsedData: Image[] | undefined = useMemo((): Image[] | undefined => {
    const modelPurpose = deployment?.applicationPurpose;
    if (
      inferenceResults &&
      deployment &&
      modelPurpose === 'Detection' &&
      inferenceResults &&
      inferenceResults !== '{}'
    ) {
      const data: DeploymentDetectionResult[] = JSON.parse(inferenceResults) || [];

      const files = deployConfig.files;

      return data.map(result => {
        let stats: DeploymentDetectionResultStats[] = result.stats || [];
        let url = '';
        let metadata: Record<string, number> | undefined = undefined;
        if (files && deployConfig.testHistoryIds?.[0] === deployment.id) {
          const currentFile = files.find(file => file.file.name === result.filename);
          url = currentFile
            ? window.URL.createObjectURL(currentFile?.file)
            : `${URL.DATASET(
                projectId,
                MODEL_SUB_OP.GET_BY_ID
              )}?id=${result.filename?.replace(',', '%252C')}&deploymentId=${
                deployment.id
              }&dataPurpose=INPUT&cachebust=${Date.now()}`;
          metadata = {
            size: currentFile?.file?.size || 0,
            width: currentFile?.metadata?.width || 0,
            height: currentFile?.metadata?.height || 0,
          };
        } else {
          url = `${URL.DATASET(
            projectId,
            MODEL_SUB_OP.GET_BY_ID
          )}?id=${result.filename?.replace(',', '%252C')}&deploymentId=${
            deployment.id
          }&dataPurpose=INPUT&cachebust=${Date.now()}`;
        }
        return {
          url,
          title: result.filename ?? '',
          metadata,
          data:
            stats?.map(item => ({
              label: startCase(toLower(item.label)),
              bbox: item.bbox,
              progress: item.progress,
            })) ?? [],
        };
      });
    }
  }, [
    deployment,
    inferenceResults,
    deployConfig.files,
    deployConfig.testHistoryIds,
    projectId,
  ]);

  const segmentationLegends = useMemo(
    () =>
      projectData.applications?.[deployment.applicationId]?.model?.segmentationLegend ||
      [],
    [projectData, deployment]
  );

  if (!inferenceResults || inferenceResults === '{}') {
    return <></>;
  }

  return (
    <>
      <div className="test-inference-results__images">
        {deployment.applicationPurpose === 'Classification' && (
          <ClassificationResult
            search={search}
            data={inferenceResults}
            projectId={projectId}
            deploymentId={deployment.id}
          />
        )}
        {(deployment.applicationPurpose === 'Segmentation' ||
          deployment.applicationPurpose === 'InstanceSegmentation') && (
          <SegmentationResult
            search={search}
            segmentationLegends={segmentationLegends}
            data={inferenceResults}
            projectId={projectId}
            deploymentId={deployment.id}
          />
        )}
        {deployment.applicationPurpose === 'PoseDetection' && (
          <PoseDetectionResult
            search={search}
            data={inferenceResults}
            projectId={projectId}
            deploymentId={deployment.id}
          />
        )}
        {deployment.applicationPurpose === 'Detection' && parsedData && (
          <DetectionResult
            search={search}
            searchEnabled={true}
            data={parsedData}
            projectId={projectId}
            deploymentId={deployment.id}
          />
        )}
      </div>
    </>
  );
};
